"use client"

import { usePathname, useSearchParams } from "next/navigation"
import Script from "next/script"
import { useEffect, useMemo, useRef } from "react"

import { buildHubSpotBootstrapScript, hsIdentify, hsTrackPageView } from "./utils"

interface HubSpotTrackingProps {
  portalId: string
}

function getCookie(name: string): string | null {
  if (typeof document === "undefined") return null
  const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`))
  return match ? match[2] : null
}

function extractEmailFromJWT(token: string): string | null {
  try {
    const parts = token.split(".")
    if (parts.length !== 3) return null
    const payload = JSON.parse(atob(parts[1]))
    return payload.email || null
  } catch {
    return null
  }
}

export default function HubSpotTracking({ portalId }: HubSpotTrackingProps) {
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const query = searchParams.toString()
  const url = query ? `${pathname}?${query}` : pathname
  const lastTrackedUrlRef = useRef(url)
  const bootstrapScriptRef = useRef(buildHubSpotBootstrapScript(url))

  const isEnabled = useMemo(() => {
    if (process.env.NODE_ENV !== "production") {
      return false
    }
    if (process.env.NEXT_PUBLIC_ENV === "test") {
      return false
    }
    // 只有在客户端才检查 hostname
    if (typeof window !== "undefined" && window.location.hostname.startsWith("test")) {
      return false
    }
    return true
  }, [])

  // 客户端 mount 后尝试从 JWT token 解析邮箱并 identify
  useEffect(() => {
    if (!isEnabled) return

    const token = getCookie("SMM_auth_token")
    if (!token) return

    const email = extractEmailFromJWT(token)
    if (email) {
      hsIdentify(email)
    }
  }, [isEnabled])

  useEffect(() => {
    if (!isEnabled) return
    if (url === lastTrackedUrlRef.current) {
      return
    }
    lastTrackedUrlRef.current = url
    hsTrackPageView(url)
  }, [isEnabled, url])

  if (!isEnabled) {
    return null
  }

  return (
    <>
      {/* HubSpot 要求这些命令在外部追踪脚本执行前同步进入 _hsq。内容由安全序列化函数生成。 */}
      {/* eslint-disable-next-line react/no-danger */}
      <script dangerouslySetInnerHTML={{ __html: bootstrapScriptRef.current }} />
      <Script id="hs-script-loader" strategy="afterInteractive" src={`https://js-na2.hs-scripts.com/${portalId}.js`} />
    </>
  )
}
